<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

function esperar(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function esperarJob(jobId, intervalo = 4000) {
  for (;;) {
    let job;

    try {
      const { data } = await api.get(`/job/${jobId}`);
      job = data.job;
    } catch (err) {
      if (err.response?.status === 404) throw new Error("El proceso expiró o no existe");
      await esperar(intervalo);
      continue;
    }

    if (job.status === "done") return job.resultado;
    if (job.status === "error") throw new Error(job.error || "Error en el proceso");

    await esperar(intervalo);
  }
}

const cargando = ref(true);
const personas = ref([]);
const batches = ref([]);
const results = ref([]);
const batchSeleccionado = ref(null);
const ejecutando = ref(false);
const showPersonaAdmin = ref(false);
const nuevaPersona = ref({ name: "", personality: "", objective: "" });

async function cargarPersonas() {
  const res = await api.get("/ai-lab/personas");
  personas.value = res.data.personas;
}

async function cargarBatches() {
  const res = await api.get("/ai-lab/batches");
  batches.value = res.data.batches;
}

async function cargarResultados(batchId) {
  batchSeleccionado.value = batchId;
  const res = await api.get("/ai-lab/results", { params: { batchId } });
  results.value = res.data.results;
}

async function alternarActiva(persona) {
  await api.put(`/ai-lab/personas/${persona.id}`, { active: !persona.active });
  persona.active = !persona.active;
}

async function crearPersona() {
  if (!nuevaPersona.value.name.trim() || !nuevaPersona.value.personality.trim() || !nuevaPersona.value.objective.trim()) return;

  try {
    await api.post("/ai-lab/personas", nuevaPersona.value);
    nuevaPersona.value = { name: "", personality: "", objective: "" };
    await cargarPersonas();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo crear la persona",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

async function eliminarPersona(persona) {
  await api.delete(`/ai-lab/personas/${persona.id}`);
  personas.value = personas.value.filter(p => p.id !== persona.id);
}

async function ejecutarPrueba() {
  ejecutando.value = true;

  try {
    const { data } = await api.post("/ai-lab/run");
    if (!data.ok) throw new Error(data.error);

    const resultado = await esperarJob(data.jobId);

    if (!resultado.batchId) {
      Swal.fire({ icon: "warning", title: "Sin personas activas", text: "Activá al menos una persona para correr la prueba." });
      return;
    }

    await cargarBatches();
    await cargarResultados(resultado.batchId);

    Swal.fire({ icon: "success", title: "Prueba completada", timer: 1800, showConfirmButton: false });

  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo correr el Laboratorio",
      text: err.message || "Error de conexión con el servidor."
    });
  } finally {
    ejecutando.value = false;
  }
}

function colorScore(score) {
  if (score === null || score === undefined) return "bg-gradient-secondary";
  if (score >= 70) return "bg-success";
  if (score >= 40) return "bg-warning text-dark";
  return "bg-danger";
}

function formatFecha(fecha) {
  return new Date(fecha).toLocaleString("es-CO");
}

onMounted(async () => {
  cargando.value = true;
  await cargarPersonas();
  await cargarBatches();
  if (batches.value.length) await cargarResultados(batches.value[0].batch_id);
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <h4 class="mb-0 lab-header-title">
        <i class="bi bi-flask me-2"></i>
        Laboratorio de IA
      </h4>

      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="showPersonaAdmin = true">
          <i class="bi bi-people me-1"></i>Personas
        </button>
        <argon-button color="success" :disabled="ejecutando" @click="ejecutarPrueba">
          <span v-if="ejecutando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-play-circle me-1"></i>
          {{ ejecutando ? "Corriendo prueba..." : "Ejecutar prueba" }}
        </argon-button>
      </div>
    </div>

    <div class="alert alert-info">
      <i class="bi bi-info-circle me-1"></i>
      Seis "clientes" simulados conversan contra tu Agente de IA en un entorno 100% interno
      (nunca toca WhatsApp/Instagram/Messenger real). Un juez LLM evalúa cada conversación
      y te da un puntaje de 0 a 100 con hallazgos y sugerencias de mejora.
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="row g-3">
      <div class="col-md-3">
        <div class="card">
          <div class="card-header">Corridas anteriores</div>
          <ul class="list-group list-group-flush">
            <li
              v-for="batch in batches"
              :key="batch.batch_id"
              class="list-group-item cursor-pointer"
              :class="{ 'bg-light': batchSeleccionado === batch.batch_id }"
              style="cursor: pointer;"
              @click="cargarResultados(batch.batch_id)"
            >
              <div class="d-flex justify-content-between align-items-center">
                <span class="small">{{ formatFecha(batch.createdAt) }}</span>
                <span class="badge" :class="colorScore(batch.average_score)">{{ batch.average_score ?? "—" }}</span>
              </div>
              <div class="text-muted small">{{ batch.total }} conversaciones</div>
            </li>
            <li v-if="batches.length === 0" class="list-group-item text-muted small">
              Todavía no corriste ninguna prueba.
            </li>
          </ul>
        </div>
      </div>

      <div class="col-md-9">
        <div v-if="results.length === 0" class="text-muted p-4 text-center">
          Seleccioná una corrida o ejecutá una prueba nueva para ver resultados.
        </div>

        <div v-for="result in results" :key="result.id" class="card mb-3">
          <div class="card-header d-flex justify-content-between align-items-center">
            <span><i class="bi bi-person-badge me-2"></i>{{ result.persona_name }}</span>
            <span class="badge" :class="colorScore(result.score)">{{ result.score ?? "sin evaluar" }}</span>
          </div>
          <div class="card-body">
            <div class="mb-2">
              <strong class="small">Hallazgos:</strong>
              <p class="small text-muted mb-1">{{ result.findings || "—" }}</p>
            </div>
            <div class="mb-3">
              <strong class="small">Sugerencias:</strong>
              <p class="small text-muted mb-1">{{ result.suggestions || "—" }}</p>
            </div>

            <details>
              <summary class="small text-primary" style="cursor: pointer;">Ver conversación simulada</summary>
              <div class="mt-2 p-2 bg-light rounded" style="max-height: 300px; overflow-y: auto;">
                <div
                  v-for="(turno, i) in result.transcript"
                  :key="i"
                  class="mb-2 d-flex"
                  :class="turno.autor === 'agente' ? 'justify-content-end' : 'justify-content-start'"
                >
                  <div class="p-2 rounded" :class="turno.autor === 'agente' ? 'bg-success-subtle' : 'bg-white border'" style="max-width: 75%;">
                    <div class="text-muted" style="font-size: 0.65rem;">{{ turno.autor === 'agente' ? 'Agente IA' : 'Cliente simulado' }}</div>
                    {{ turno.texto }}
                  </div>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL PERSONAS -->
    <div v-if="showPersonaAdmin" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5>Personas simuladas</h5>
            <button class="btn-close" @click="showPersonaAdmin = false"></button>
          </div>
          <div class="modal-body">
            <ul class="list-group mb-3">
              <li v-for="persona in personas" :key="persona.id" class="list-group-item">
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <strong>{{ persona.name }}</strong>
                    <div class="text-muted small">{{ persona.personality }}</div>
                    <div class="text-muted small"><em>Objetivo:</em> {{ persona.objective }}</div>
                  </div>
                  <div class="d-flex align-items-center gap-2 flex-shrink-0">
                    <div class="form-check form-switch mb-0">
                      <input class="form-check-input" type="checkbox" role="switch" :checked="persona.active" @change="alternarActiva(persona)">
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="eliminarPersona(persona)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </li>
            </ul>

            <h6>Agregar persona</h6>
            <div class="mb-2">
              <argon-input v-model="nuevaPersona.name" placeholder="Nombre, ej. Cliente apurado" />
            </div>
            <div class="mb-2">
              <textarea v-model="nuevaPersona.personality" class="form-control" rows="2" placeholder="Personalidad (system prompt del cliente simulado)"></textarea>
            </div>
            <div class="mb-2">
              <argon-input v-model="nuevaPersona.objective" placeholder="Objetivo de la conversación" />
            </div>
            <argon-button color="success" class="w-100" @click="crearPersona">
              <i class="bi bi-plus-circle me-1"></i>Agregar
            </argon-button>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showPersonaAdmin = false">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showPersonaAdmin" class="modal-backdrop fade show"></div>

  </div>
</template>

<style scoped>
.lab-header-title {
  color: #fff !important;
}
</style>
